import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Typography,
  Box,
} from "@mui/material";
import { PrimaryButton } from "../../../components/Buttons/PrimaryButton";
import { SecondaryButton } from "../../../components/Buttons/SecondaryButton";
import { Style } from "../../../../core/models/entities/Style";
import { StyleView } from "../../../../core/models/entities/StyleView";
import { useRepositoriesContext } from "../../../../contexts/RepositoriesContext";

interface BulkPpcmEditDialogProps {
  open: boolean;
  onClose: () => void;
  selectedStyles: Style[];
  onUpdateComplete: () => void;
}

interface ViewPpcmValues {
  front?: number;
  back?: number;
  left?: number;
  right?: number;
}

interface ViewCheckboxState {
  front: boolean;
  back: boolean;
  left: boolean;
  right: boolean;
}

export function BulkPpcmEditDialog({
  open,
  onClose,
  selectedStyles,
  onUpdateComplete,
}: BulkPpcmEditDialogProps) {
  const { styleRepository } = useRepositoriesContext();
  const [ppcmValues, setPpcmValues] = useState<ViewPpcmValues>({});
  const [checkboxState, setCheckboxState] = useState<ViewCheckboxState>({
    front: false,
    back: false,
    left: false,
    right: false,
  });
  const [isUpdating, setIsUpdating] = useState(false);
  const [availableViews, setAvailableViews] = useState<{
    front: boolean;
    back: boolean;
    left: boolean;
    right: boolean;
  }>({
    front: false,
    back: false,
    left: false,
    right: false,
  });

  useEffect(() => {
    if (open && selectedStyles.length > 0) {
      // Determine which views are available in the selected styles
      const viewsAvailable = {
        front: selectedStyles.some((style) =>
          style.styleViews?.some((view: StyleView) => view.view === "Front")
        ),
        back: selectedStyles.some((style) =>
          style.styleViews?.some((view: StyleView) => view.view === "Back")
        ),
        left: selectedStyles.some((style) =>
          style.styleViews?.some((view: StyleView) => view.view === "Left")
        ),
        right: selectedStyles.some((style) =>
          style.styleViews?.some((view: StyleView) => view.view === "Right")
        ),
      };

      setAvailableViews(viewsAvailable);
      setPpcmValues({});
      setCheckboxState({
        front: false,
        back: false,
        left: false,
        right: false,
      });
    }
  }, [open, selectedStyles]);

  const handlePpcmChange = (viewType: keyof ViewPpcmValues, value: string) => {
    const numValue = value === "" ? undefined : parseFloat(value);
    setPpcmValues((prev) => ({
      ...prev,
      [viewType]: numValue,
    }));
  };

  const handleCheckboxChange = (viewType: keyof ViewCheckboxState) => {
    setCheckboxState((prev) => ({
      ...prev,
      [viewType]: !prev[viewType],
    }));
  };

  const handleUpdate = async () => {
    if (!Object.values(checkboxState).some((val) => val)) {
      // No views selected for update
      return;
    }

    setIsUpdating(true);

    try {
      const updatePromises = selectedStyles.map(async (style) => {
        const updatedViews = style.styleViews?.map((view: StyleView) => {
          const viewType = view.view.toLowerCase() as keyof ViewPpcmValues;

          if (checkboxState[viewType] && ppcmValues[viewType] !== undefined) {
            return {
              ...view,
              ppcm: ppcmValues[viewType]!,
            };
          }
          return view;
        });

        // Update the style with modified views
        await styleRepository.updateStyle({
          styleId: style.id,
          name: style.name,
          model: style.model,
          views: updatedViews?.map((view) => ({
            viewId: view.id,
            ppcm: view.ppcm,
          })),
        });
      });

      await Promise.all(updatePromises);
      onUpdateComplete();
      onClose();
    } catch (error) {
      console.error("Error updating PPCM values:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  const getViewDisplayName = (viewType: string) => {
    switch (viewType) {
      case "front":
        return "Front";
      case "back":
        return "Back";
      case "left":
        return "Left";
      case "right":
        return "Right";
      default:
        return viewType;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Bulk Update PPCM - {selectedStyles.length} Selected Styles
      </DialogTitle>
      <DialogContent sx={{ minHeight: "600px" }}>
        <Box sx={{ display: "flex", gap: 4, height: "100%" }}>
          {/* PPCM Edit Section */}
          <Box
            sx={{ display: "flex", flexDirection: "column", gap: 2, flex: 1 }}
          >
            <Typography variant="body2" color="text.secondary">
              Change the PPCM value of each style below. <br /> Keep in mind
              that a higher PPCM value will display the logos as larger, and a
              lower PPCM value will display the logos as smaller.
            </Typography>

            {(["front", "back", "left", "right"] as const).map((viewType) => {
              if (!availableViews[viewType]) return null;

              return (
                <Box
                  key={viewType}
                  sx={{ display: "flex", alignItems: "center", gap: 2 }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={checkboxState[viewType]}
                        onChange={() => handleCheckboxChange(viewType)}
                      />
                    }
                    label={getViewDisplayName(viewType)}
                  />
                  <TextField
                    type="number"
                    size="small"
                    label="PPCM"
                    value={ppcmValues[viewType] || ""}
                    onChange={(e) => handlePpcmChange(viewType, e.target.value)}
                    disabled={!checkboxState[viewType]}
                    inputProps={{
                      min: 0,
                      step: 0.1,
                      style: { width: "100px" },
                    }}
                  />
                </Box>
              );
            })}

            {!Object.values(availableViews).some((val) => val) && (
              <Typography variant="body2" color="text.secondary">
                No views available in the selected styles.
              </Typography>
            )}
          </Box>

          {/* Selected Styles List */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              flex: 1,
              minHeight: "100%",
              overflowY: "auto",
            }}
          >
            <Typography variant="subtitle2" color="text.primary">
              Selected Styles ({selectedStyles.length})
            </Typography>

            {selectedStyles.map((style) => (
              <Box
                key={style.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  p: 1,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                }}
              >
                <img
                  alt={style.name}
                  src={style.thumbnailUrl}
                  className="object-contain bg-white rounded hover:border hover:p-1 max-h-[74px] origin-left hover:scale-[2.5] hover:ease-out transition-all duration-150 ease-out"
                />
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {`${style.name} - ${style.model}`}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ padding: "12px 24px" }}>
        <SecondaryButton onClick={onClose} disabled={isUpdating}>
          Cancel
        </SecondaryButton>
        <PrimaryButton
          onClick={handleUpdate}
          disabled={
            isUpdating || !Object.values(checkboxState).some((val) => val)
          }
        >
          {isUpdating ? "Updating..." : "Update PPCM"}
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  );
}
